import { useState } from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import CustomButton from "../customs/custom-button";
import { ParseErrors } from "../../utils/ParseErrors";
import {
  cashTotal,
  money,
  PAYMENT_TERMS,
  PublicChase,
} from "./payment-page.constants";

interface IStripeNonMotoForm {
  chase: PublicChase;
  onBack: () => void;
}

const API_URL = process.env.REACT_APP_API_URL;

const StripeNonMotoForm = ({ chase, onBack }: IStripeNonMotoForm) => {
  const stripe = useStripe();
  const elements = useElements();
  const [value, setValue] = useState({
    isCardGuarantue: false,
    termsConditions: false,
    loading: false,
  });
  const set = (patch: Partial<typeof value>) =>
    setValue((s) => ({ ...s, ...patch }));

  // ------------------------------- PAY (NON MOTO — CUSTOMER PRESENT) -------------------------------
  const handlePayNow = async () => {
    const unTicked = PAYMENT_TERMS.find(
      (term) => !value[term.key as keyof typeof value],
    );

    if (unTicked) return toast.error(unTicked.label);
    if (!stripe || !elements) return;

    set({ loading: true });
    try {
      // Stripe redirects the customer to the server, which pays the order and sends them back.
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // The version comes back with the customer, so the confirm lands on
          // this chase and not a sibling on the same policy reference.
          return_url: `${API_URL}/pub/payment/stripe/confirm/${chase.policyReferenceNumber}${
            chase.versionReferenceNumber
              ? `?version=${encodeURIComponent(chase.versionReferenceNumber)}`
              : ""
          }`,
        },
      });

      if (result.error) toast.error(result.error.message || "Payment failed");
      set({ loading: false });
    } catch (error) {
      toast.error(ParseErrors(error));
      set({ loading: false });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* What they're about to be charged — carried through from the summary. */}
      <div
        className="flex items-center justify-between gap-4 rounded-2xl border px-4 py-3"
        style={{
          background: "var(--brand-soft)",
          borderColor: "var(--brand-edge)",
        }}
      >
        <p className="text-label-muted text-[11px] font-semibold uppercase tracking-[1.2px]">
          Paying today
        </p>
        <p
          className="text-[19px] font-bold tabular-nums leading-none"
          style={{ color: "var(--brand)" }}
        >
          {money(cashTotal(chase))}
        </p>
      </div>

      <PaymentElement options={{ layout: "tabs" }} />

      {/* ---------------------------- TERMS ---------------------------- */}
      <div className="flex flex-col gap-3 border-t border-dashed border-edge pt-4">
        {PAYMENT_TERMS.map((term) => (
          <label
            key={term.key}
            className="flex items-start gap-3 cursor-pointer text-text-secondary text-sm"
          >
            <input
              type="checkbox"
              className="mt-[3px] h-4 w-4 shrink-0 cursor-pointer"
              style={{ accentColor: "var(--brand)" }}
              checked={!!value[term.key as keyof typeof value]}
              onChange={(e) => set({ [term.key]: e.target.checked } as any)}
            />
            <span>{term.label}</span>
          </label>
        ))}
      </div>

      {/* ---------------------------- ACTIONS ---------------------------- */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="w-1/3 h-[50px] rounded-lg border border-edge text-text-secondary text-sm font-medium disabled:opacity-50"
          onClick={onBack}
          disabled={value.loading}
        >
          Back
        </button>
        <CustomButton
          variant="primary"
          fullWidth
          loading={value.loading}
          onClick={handlePayNow}
        >
          {`Pay ${money(cashTotal(chase))}`}
        </CustomButton>
      </div>

      <p className="text-text-secondary text-center text-xs">
        Payments are processed securely by Stripe.
      </p>
    </div>
  );
};

export default StripeNonMotoForm;
